import {
    ProcedureArgsObject,
    ProcedureName,
    LoginSuccessResponseStatus,
    ProcedureArgs,
    ULRpcRequestMessage,
    ULRpcResponseMessage,
    ULRpcResponseErrorMessage,
    SocialLoginConfig,
    PasskeyRegistrationExitState,
    LoginSuccessAdditional,
} from '@ul/common';
import { BaseRpcCaller } from './BaseRpcCaller';
import { WithULRpcCaller } from './WithULRpcCaller';

declare global {
    interface Window {
        webkit: {
            messageHandlers: Partial<Record<ProcedureName, { postMessage: Function }>>;
        };
        ULRpcResponse: Record<string, Function>;
    }
}

class IOSWebviewRpcCaller<A extends ProcedureArgs = ProcedureArgsObject> extends BaseRpcCaller<A> {
    constructor(rpcId: string, target: Window = window) {
        super(rpcId);
        this.target = target;

        this.target.ULRpcResponse = this.target.ULRpcResponse || {};
        this.responseNamespace = this.target.ULRpcResponse;
    }

    private target: Window;
    private responseNamespace: Record<string, Function>;

    protected sendRequest = <P extends ProcedureName>(request: ULRpcRequestMessage<P, A>): boolean => {
        // Trigger procedure in iOS injected namespace
        const handler = this.target.webkit?.messageHandlers[request.procedureName];
        if (!handler) {
            return false;
        }
        handler.postMessage(JSON.stringify(request));
        return true;
    };

    protected receiveResponse = <P extends ProcedureName>(
        request: ULRpcRequestMessage<P, A>,
        resolve: (value: ULRpcResponseErrorMessage | ULRpcResponseMessage<P>) => void
    ) => {
        // Creates global function window.ULRpcResponse[<request.nonce>] for webview eval
        this.responseNamespace[request.nonce] = (data: string) => {
            resolve(new ULRpcResponseMessage(request.procedureName, JSON.parse(data), request.nonce, request.appId));
        };
        return () => delete this.responseNamespace[request.nonce];
    };
}

export class IOSWebviewULRpcCaller extends WithULRpcCaller(IOSWebviewRpcCaller) {
    /**********************************
     ****** UL RPC caller methods *****
     **********************************/

    public onExit = () => this.call('onExit', {});

    public onLoginSuccess = async (additional?: LoginSuccessAdditional) => {
        this.call('onLoginSuccess', { additional });
        return { loginStatus: LoginSuccessResponseStatus.Success };
    };

    public onNotificationPush = (message: string, isSuccess: boolean) =>
        this.call('onNotificationPush', { message, isSuccess });

    public onSpaLoad = (spaLoadCompletedAt: number) => this.call('onSpaLoad', { spaLoadCompletedAt });

    public onSocialLogin = async (config: SocialLoginConfig) => this.callAsync('onSocialLogin', { config }, 3600000);

    public onSpaWillLoad = async () => this.callAsync('onSpaWillLoad', {}, 10000);

    public onDeleteAccountSuccess = () => this.call('onDeleteAccountSuccess', {});

    public onDeleteAccountExit = () => this.call('onDeleteAccountExit', {});

    public onForceLogoutSuccess = () => this.call('onForceLogoutSuccess', {});

    public onForceLogoutExit = () => this.call('onForceLogoutExit', {});

    public onDismissButtonClick = () => this.call('onDismissButtonClick', {});

    public onVerifyOtp = (message: string, isSucces: boolean, status: number) =>
        this.call('onVerifyOtp', { message, isSucces, status });

    public onWalletAuthenticated = (success: boolean) => this.call('onWalletAuthenticated', { success });

    public onTransactionWillLoad = async () => this.callAsync('onTransactionWillLoad', {}, 10000);

    public onPasskeyRegister = async (challengeOptions: any) =>
        this.callAsync('onPasskeyRegister', { challengeOptions }, 60000);

    public onPasskeyAuthenticate = async (challengeOptions: any, useConditionalMediation?: boolean) =>
        this.callAsync('onPasskeyAuthenticate', { challengeOptions, useConditionalMediation }, 60000);

    public onPasskeyRegistrationExit = (exitState: PasskeyRegistrationExitState) =>
        this.call('onPasskeyRegistrationExit', { exitState });

    public onPasskeySupportsAutofill = async () => this.callAsync('onPasskeySupportsAutofill', {}, 60000);
}
